import { useMutation } from '@apollo/client/react';
import { useAuth } from 'react-oidc-context';
import { AisExportType } from '../graphql/generated/graphql.ts';
import { hasSomeRole, Role } from '../utils/user.ts';
import { useCurrentContextStore } from './store/useCurrentContextStore.ts';
import { MARK_AIS_EXPORTED_MUTATION } from './useAisExport.gql';
import { useDoctorAisExportTypes } from './useDoctorAisExportTypes';

type AisExportResult = {
  encodedFile: string;
  filename: string;
  mimeType: string;
};

type AisExportableRequest = {
  id: string;
  orders: Array<{
    aisExportedAt?: string | null;
  }>;
  additionalData: Record<string, string>;
};

const OUT_PDF_KEY = 'Out-Pdf';

export const useAisExport = (): ((
  request: AisExportableRequest,
  orderIds: string[], // empty array means all orders that are not already exported
  onError: (msg: string) => void,
  onSuccess: () => void
) => void) => {
  const auth = useAuth();
  const isLabRole = hasSomeRole([Role.ROLE_LR_FRONT_DESK, Role.ROLE_LR_LAB_ADMIN, Role.ROLE_LR_LAB_STAFF], auth.user);
  const { currentDoctorId, primaryDoctorId } = useCurrentContextStore();
  const doctorId = isLabRole ? primaryDoctorId : currentDoctorId;
  const doctorAisExportTypes = useDoctorAisExportTypes(doctorId);
  const [markAisExportedMutation] = useMutation(MARK_AIS_EXPORTED_MUTATION);

  return async (request: AisExportableRequest, orderIds: string[], onError: (msg: string) => void, onSuccess: () => void) => {
    if (!window.nativeApi) {
      return;
    }

    const exportNeeded = orderIds.length > 0 || request.orders.some(it => !it.aisExportedAt);
    if (!exportNeeded) {
      return;
    }

    const aisExportTypes = [...doctorAisExportTypes];
    const commonPdfExportEnabled = aisExportTypes.includes(AisExportType.PDF);
    if (!commonPdfExportEnabled && request.additionalData[OUT_PDF_KEY]) {
      aisExportTypes.push(AisExportType.PDF);
    }

    if (!aisExportTypes.length) {
      return;
    }

    for (const aisExportType of aisExportTypes) {
      const orderIdsParam = orderIds.map(id => `orderIds=${encodeURIComponent(id)}`).join('&');
      const endpoint =
        window._env_.API_URL + `/rest/ais-export?requestId=${request.id}&type=${aisExportType}${orderIdsParam ? `&${orderIdsParam}` : ''}`;
      try {
        const response = await fetch(endpoint, {
          headers: {
            authorization: `Bearer ${auth.user?.access_token}`,
          },
        });

        if (!response.ok) {
          console.error('AIS export server response not ok');
          onError('Server-Antwort ist nicht OK');
          return;
        }

        const result = (await response.json()) as AisExportResult[];
        for (const aisExport of result) {
          const buffer = Uint8Array.from(window.atob(aisExport.encodedFile), c => c.charCodeAt(0));

          if (aisExportType === AisExportType.PDF) {
            if (commonPdfExportEnabled) {
              await window.nativeApi.aisExport(aisExport.filename, buffer);
            }
            if (request.additionalData[OUT_PDF_KEY]) {
              await window.nativeApi.aisExportPdf(request.additionalData[OUT_PDF_KEY], buffer);
            }
          } else {
            await window.nativeApi.aisExport(aisExport.filename, buffer);
          }
        }
      } catch (e) {
        console.error('AIS export failed', e);
        if (e instanceof Error) {
          onError(e.message);
        } else {
          onError('Details unbekannt');
        }
        return;
      }
    }

    try {
      await markAisExportedMutation({
        variables: {
          requestId: request.id,
          orderIds: orderIds,
        },
      });

      onSuccess();
    } catch (e) {
      console.error('Marking AIS export failed', e);
      onError('Anforderung konnte nicht als exportiert markiert werden');
    }
  };
};
